import React, { useRef } from "react";

import { LuX } from "react-icons/lu";
import styled from "styled-components";

import { alertDialog } from "shared/ui/feedback";

const DEFAULT_ACCEPT = "image/jpeg,image/jpg,image/png";
const DEFAULT_MAX_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_TYPES = ["jpeg", "jpg", "png"];

/**
 * 썸네일/배너/팝업 이미지 삽입 공용 박스.
 *
 * 등록·수정 어디서든 동일하게 쓰는 단일 이미지 업로더. 박스 전체 클릭 또는
 * 드래그&드롭으로 파일을 고르면 타입/용량 검사를 통과한 File을 onSelectFile로
 * 넘긴다(상태는 부모가 소유). 미리보기 상태에서는 우상단 X로 제거한다.
 *
 * @param {string} imageUrl      미리보기 URL(빈 값이면 안내 상태)
 * @param {(file:File)=>void} onSelectFile  검사 통과한 파일 전달
 * @param {()=>void} onRemove    미리보기 제거
 * @param {string} title         안내 헤더 (예: "썸네일 이미지 삽입")
 * @param {boolean} required     헤더에 빨간 * 표시 (기본 true)
 * @param {string} sizeHint      두 번째 안내 줄 (예: "이미지 사이즈(250*250 권장)")
 * @param {string} width         박스 가로 (기본 238px)
 * @param {string} height        박스 세로 (기본 238px)
 * @param {string} accept        input accept (기본 jpeg/jpg/png)
 * @param {number} maxSize       최대 용량(byte). null이면 검사 안 함 (기본 10MB)
 * @param {string} alt           미리보기 alt
 */
export function ImageUploadBox({
  imageUrl,
  onSelectFile,
  onRemove,
  title = "이미지 삽입",
  required = true,
  sizeHint,
  width = "238px",
  height = "238px",
  accept = DEFAULT_ACCEPT,
  maxSize = DEFAULT_MAX_SIZE,
  alt = "uploaded",
  className,
}) {
  const inputRef = useRef(null);

  const processFile = (file) => {
    if (!file) return;
    if (maxSize != null && file.size > maxSize) {
      alertDialog("파일 크기가 10MB를 초과합니다. 더 작은 파일을 선택해주세요.");
      return;
    }
    const fileType = (file.type.split("/")[1] || "").toLowerCase();
    if (!ALLOWED_TYPES.includes(fileType)) {
      alertDialog(".jpeg, .jpg, .png 파일만 업로드 가능합니다");
      return;
    }
    onSelectFile?.(file);
  };

  const handleInputChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = ""; // 같은 파일 재선택 시에도 onChange 발화
    processFile(file);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    if (imageUrl) return; // 미리보기 중에는 드롭 무시(제거 후 다시)
    processFile(event.dataTransfer.files?.[0]);
  };

  const openPicker = () => inputRef.current?.click();

  return (
    <Box
      className={className}
      $width={width}
      $height={height}
      onClick={imageUrl ? undefined : openPicker}
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDrop}
      $clickable={!imageUrl}
    >
      {imageUrl ? (
        <Preview>
          <PreviewImg src={imageUrl} alt={alt} />
          <RemoveButton
            type="button"
            aria-label="이미지 삭제"
            onClick={(e) => {
              e.stopPropagation();
              onRemove?.();
            }}
          >
            <LuX size={14} />
          </RemoveButton>
        </Preview>
      ) : (
        <>
          <Header>
            {title}
            {required && <RedAsterisk>&nbsp;*</RedAsterisk>}
          </Header>
          <Hint>
            드래그 업로드 또는 내 PC 검색
            {sizeHint && (
              <>
                <br />
                {sizeHint}
              </>
            )}
          </Hint>
          <PickLabel as="span">내 PC</PickLabel>
        </>
      )}
      <HiddenInput
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleInputChange}
      />
    </Box>
  );
}

export default ImageUploadBox;

const Box = styled.div`
  position: relative;
  width: ${({ $width }) => $width};
  height: ${({ $height }) => $height};
  border-radius: 5px;
  border: 1px solid var(--admin-gray-stroke, #e8e8e8);
  background: var(--white, #fff);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};
  overflow: hidden;
`;

const Header = styled.div`
  font-size: ${(props) => props.theme.Web_fontSizes.Body3};
  font-weight: ${(props) => props.theme.fontWeights.Body3};
  line-height: ${(props) => props.theme.LineHeight.Body3};
  letter-spacing: -0.56px;
  font-style: normal;
  font-family: "Noto Sans KR";
  color: var(--black, #000);
  margin-bottom: 11px;
`;

const RedAsterisk = styled.span`
  color: #ff1717;
`;

const Hint = styled.div`
  font-size: ${(props) => props.theme.Web_fontSizes.Caption2};
  font-weight: ${(props) => props.theme.fontWeights.Caption2};
  line-height: ${(props) => props.theme.LineHeight.Caption2};
  font-style: normal;
  font-family: "Noto Sans KR";
  color: var(--admin-gray-disabled-button, #cfd4d9);
  margin-bottom: 30px;
`;

const PickLabel = styled.label`
  width: 100px;
  padding: 5px;
  border-radius: 5px;
  border: 1px solid var(--admin-gray-stroke, #e8e8e8);
  background: var(--white, #fff);
  color: var(--black, #000);
  text-align: center;
  cursor: pointer;
  font-size: ${(props) => props.theme.Web_fontSizes.Body3};
  font-weight: ${(props) => props.theme.fontWeights.Body3};
  line-height: ${(props) => props.theme.LineHeight.Body3};
`;

const HiddenInput = styled.input`
  display: none;
`;

const Preview = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const PreviewImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 6px;
  right: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  color: #333333;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  cursor: pointer;
`;
